<template>
  <master-layout pageTitle="Customers">
    <div class="customers-info">
      <div v-show="customerView === 'customer-list-view'" class="customer-list-container">
        <!-- Search Bar + Import actions row -->
        <div class="customer-search-container">
          <!-- CommonSearchFilter -->
          <CommonSearchFilter
            ref="customerSearchFilter"
            :anySearchOrFilterIsApplied="anySearchOrFilterIsApplied"
            :filteredChannels="filteredChannels"
            :showFilterOptions="false"
            :showClearFilterBar="false"
            :showSearchByTitle="false"
            :showChannelSearch="true"
            :showSubscriptionSearch="true"
            :showSegmentSearch="true"
            :segmentList="segmentList ?? []"
            :segmentListLoading="segmentListLoading"
            placeholder="Search customers"
            @search="onSearchBarSearch"
            @clear="onSearchBarClear"
            class="customer-common-search"
          />
          <!-- Import CSV (desktop) -->
          <div class="desktop-only importContainer">
            <div v-if="whatsappConnected || googlercsConnected || smsConnected" class="flex-between gap10P">
              <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Import status" @click="openImportStatus()"></VButton>
              <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Import customers" @click="isOpenImportModal = true"></VButton>
            </div>
            <VButton v-if="customersResults.length > 0" class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Export customers" @click="exportCustomers"></VButton>
          </div>

          <!-- Import / Export actions (mobile) -->
          <div class="mobile-only">
            <div class="circle-wrapper">
              <ActionPopOver :style="{ '--width': '215px' }">
                <div class="listContainer">
                  <div v-if="whatsappConnected || googlercsConnected || smsConnected">
                    <button class="popover-item" @click="openImportStatus()">Import Status</button>
                    <button class="popover-item" @click="isOpenImportModal = true">Import customers</button>
                  </div>
                  <button v-if="customersResults.length > 0" class="popover-item" @click="exportCustomers">Export customers</button>
                </div>
              </ActionPopOver>
            </div>
          </div>
        </div>

        <div class="customer-listings-wrapper">
          <div class="customer-listings-content" :class="{ 'search-mode': isSearchCustomers }">
            <!-- Stats row -->
            <div v-if="!isSearchCustomers" class="count">
              <div class="customerFlex" :class="{ iconDivider: !isFilteredByChannel }">
                <VIcon name="group-line" :size="-5" class="iconPrimary" />
                <div class="description" style="width: 120px;">
                  <h2 class="ion-no-margin sub-title custPadding">Total customers</h2>
                  <p v-if="statsLoading" class="ion-no-margin sub-header">
                    <span class="skeleton-text" style="width: 60px; height: 16px;"></span>
                  </p>
                  <p v-else class="ion-no-margin sub-header">{{ formattedTotalCustomers }}</p>
                </div>
              </div>
              <div v-if="!isFilteredByChannel" class="customerFlex salesChannelWrapper">
                <div class="salesChannleList">
                  <template v-if="statsLoading">
                    <div v-for="n in 3" :key="n" class="customerFlex">
                      <span class="skeleton-text skeleton-circle" style="width: 25px; height: 25px;"></span>
                      <div class="description">
                        <span class="skeleton-text" style="width: 70px; height: 14px; margin-bottom: 6px;"></span>
                        <span class="skeleton-text" style="width: 40px; height: 14px;"></span>
                      </div>
                    </div>
                  </template>
                  <template v-else v-for="channel in channelWiseCustomers" :key="channel.value">
                    <div class="customerFlex cursorPointer" @click="filterChannel(channel.value, channel.count)">
                      <img :src="channel.icon" class="channelIcon" alt="channel_icon" />
                      <div class="description">
                        <h2 class="ion-no-margin sub-title custPadding">{{ channel.name }}</h2>
                        <h2 class="ion-no-margin sub-header">{{ channel.count }}</h2>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Custom data / tags summary -->
            <div v-if="!isSearchCustomers" class="flex-between">
              <div class="count flex100P flexContainer">
                <div class="customerFlex">
                  <VIcon name="inbox-3-line" class="iconPrimary" />
                  <div class="description" style="width: 120px;">
                    <h2 class="ion-no-margin sub-title custPadding">Custom data</h2>
                    <div class="flex-start">
                      <template v-if="customDataLoading">
                        <span class="skeleton-text" style="width: 60px; height: 16px;"></span>
                      </template>
                      <template v-else>
                        <p class="ion-no-margin sub-header">{{ total.customFields }}</p>
                        <!-- <HelpTooltip v-if="total.customFields === 240" :iconStyle="{ color: '#FA7554' }">Limit Reached</HelpTooltip> -->
                      </template>
                    </div>
                  </div>
                </div>
                <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Show all" @click="showAll('fields')"></VButton>
              </div>
              <div class="count flex100P flexContainer">
                <div class="customerFlex">
                  <VIcon name="tag-line" class="iconPrimary" />
                  <div class="description" style="width: 120px;">
                    <h2 class="ion-no-margin sub-title custPadding">Custom tags</h2>
                    <div class="flex-start">
                      <template v-if="customDataLoading">
                        <span class="skeleton-text" style="width: 60px; height: 16px;"></span>
                      </template>
                      <template v-else>
                        <p class="ion-no-margin sub-header">{{ total.tags }}</p>
                        <!-- <HelpTooltip v-if="total.tags === 60" :iconStyle="{ color: '#FA7554' }">Limit Reached</HelpTooltip> -->
                      </template>
                    </div>
                  </div>
                </div>
                <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Show all" @click="showAll('tags')"></VButton>
              </div>
            </div>

            <!-- Customer table -->
            <div v-if="customersLoading || customersResults.length > 0" class="inner-container customer-list-scrollable">
              <CustomerListTable
                :customers="customersResults"
                :loading="customersLoading"
                @open-detail="openDetail"
                @view-conversation="(id) => $router.push({ path: '/page-inbox', query: { conversation: id } })"
                @delete-customer="deleteCustomer"
              />
              <div class="sticky-pagination">
                <pagination
                  class="pagination-wrapper"
                  :totalCount="formattedTotalCustomers"
                  :currentPage="currentPaginationNumber"
                  :pageSize="pageSize"
                  :loading="customersLoading"
                  @change="loadPage"
                  configurablePageSize
                />
              </div>
            </div>
            <div class="text-center loadingScreen" v-if="!customersLoading && customersResults.length === 0">
              <div>
                <img src="@/resources/images/not-found.svg" class="emptyImg" alt="not_found" />
                <h5>No customers yet! 😟</h5>
                <p class="vtext-subtitle">Seems like the one you're looking for, hasn't interacted with you yet</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer detail view -->
      <div v-if="customerView === 'customer-info-view'">
        <div class="chat-toolbar">
          <div class="toolbar-start">
            <VIcon @click="onCustomerDetailBack" name="arrow-left-line" :size="-4" class="cursorPointer" />
            <h2 class="prdt-title ion-no-margin" style="padding-left; 0px;">Customer profile</h2>
          </div>
        </div>
        <div class="detailCustomer">
          <single-user-detail
            :conversationid="singleUserDetailCustomer?.conversationid"
            :activeCustomer="detailCustomer"
            @user_info_updated="refreshAll"
            @resolved_chat="refreshAll"
            :agentReAssignmentAllowed="false"
            crm
          />
        </div>
      </div>
    </div>

    <UploadCSVComponent v-if="isOpenImportModal" type="customers" name="Import customers" :isOpenImportModal="isOpenImportModal" @update:isOpenImportModal="(val) => isOpenImportModal = val" :instructions="instructions" :showInstructions="true" acceptTypes=".csv, .xls, .xlsx, .tsv" :showDuplicateColumn="true" @openImportStatusBttn="openImportStatus"/>
    
    <ImportStatusModal
      v-if="isOpenImportStatusModal"
      :isOpen="isOpenImportStatusModal"
      :uploadedListOfFiles="uploadedListOfFiles"
      :uploadedFilesLoader="uploadedFilesLoader"
      @dismiss="isOpenImportStatusModal = false"
      @download-failed="downloadFailedImport"
    />

    <CustomerDeleteModal
      v-if="deleteCustomerConversationId !== undefined"
      :isOpen="deleteCustomerConversationId !== undefined"
      @confirm="confirmDeleteCustomer"
      @cancel="deleteCustomerConversationId = undefined"
    />

    <CustomDataTagsModal
      v-if="openCustomDetailsModal"
      :isOpen="openCustomDetailsModal"
      :allFields="allFields"
      :allTags="allTags"
      :total="total"
      :loading="customDataLoading"
      v-model:activeTab="activeTab"
      @dismiss="openCustomDetailsModal = false"
      @delete-tag="handleDeleteTag"
      @delete-field="handleDeleteField"
    />
    <exports-modal-v2 v-if="csvExportWithDateRangeModal" :type="csvExportWithDateRangeModal_type" :isOpen="csvExportWithDateRangeModal" :title="csvExportWithDateRangeModal_title" :downloadID="downloadID" :fileName="fileName" @close="() => csvExportWithDateRangeModal = false"></exports-modal-v2>
  </master-layout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useUIStore } from '@/stores/ui'
import { useSocialConnections } from '@/composables/useSocialConnections'

import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'

import whatsappIcon from '@/resources/images/whatsapp_icon.svg'
import smsLogo from '@/resources/images/sms_logo.svg'
import googleRcsIcon from '@/resources/images/Google_RCS_icon.svg'

// ── Components ────────────────────────────────────────────────────────────────
import pagination from '@/components/pagination.vue'
import exportsModalV2 from '@/components/exportModalV2.vue'
import ActionPopOver from '@/components/ActionPopOver'
import UploadCSVComponent from '@/components/upload-csv-component.vue'
import CommonSearchFilter from '@/components/CommonSearchFilter.vue'
import singleUserDetail from '@/components/singleUserDetail'
import HelpTooltip from '@/components/HelpTooltip.vue'
import CustomerListTable from './components/CustomerListTable.vue'
import ImportStatusModal from './components/ImportStatusModal.vue'
import CustomerDeleteModal from './components/CustomerDeleteModal.vue'
import CustomDataTagsModal from './components/CustomDataTagsModal.vue'

// ── Composables ───────────────────────────────────────────────────────────────
import { useCustomerStats } from './composables/useCustomerStats'
import { useCustomerList } from './composables/useCustomerList'
import { useCustomDataTags } from './composables/useCustomDataTags'
import { useCustomerDetail } from './composables/useCustomerDetail'
import { useSegments } from '@/composables/useSegments'
import { useImportExport } from './composables/useImportExport'

// ── Global helpers ─────────────────────────────────────────────────────────────
const updateLoading = (payload) => useUIStore().updateLoading(payload);
const updateToast = (payload) => useUIStore().updateToast(payload);
const { isWhatsAppConnected: whatsappConnected, isSMSConnected: smsConnected, isGooglercsConnected: googlercsConnected } = useSocialConnections()

// ── Composable instances ──────────────────────────────────────────────────────
const {
  totalCustomers,
  filteredTotalCustomers,
  channelWiseCustomers,
  statsLoading,
  fetchTotalStats,
  fetchFilteredStats,
  setFilteredTotal,
} = useCustomerStats()

const {
  customers: customersResults,
  loading: customersLoading,
  currentPage: currentPaginationNumber,
  currentPageSize: pageSize,
  isSearchMode: isSearchCustomers,
  isChannelFiltered: isFilteredByChannel,
  fetchList,
  clearList,
} = useCustomerList()

const {
  total,
  allTags,
  allFields,
  customDataLoading,
  openCustomDetailsModal,
  activeTab,
  fetchTagsCount,
  fetchCustomFieldsCount,
  showAll,
  handleDeleteTag,
  handleDeleteField,
} = useCustomDataTags()

const {
  customerView,
  detailCustomer,
  singleUserDetailCustomer,
  openDetail,
  backToList,
} = useCustomerDetail()

const { data: segmentList, isPending: segmentListLoading } = useSegments()

const {
  isOpenImportModal,
  isOpenImportStatusModal,
  uploadedListOfFiles,
  uploadedFilesLoader,
  csvExportWithDateRangeModal,
  csvExportWithDateRangeModal_type,
  csvExportWithDateRangeModal_title,
  downloadID,
  fileName,
  openImportStatus,
  downloadFailedImport,
  openExportCustomers,
  notifyExportUnavailable,
} = useImportExport()

// ── Template refs ─────────────────────────────────────────────────────────────
const customerSearchFilter = ref(null)

// ── Search / filter state ─────────────────────────────────────────────────────
const search = reactive({
  searchByFirstName: '',
  searchByLastName: '',
  searchByEmail: '',
  searchByMobile: '',
  searchByTagText: '',
  searchByChannelText: '',
  searchBySubscriptionStatus: '',
  searchByCustomFieldKey: '',
  searchByCustomFieldText: '',
  segment: '',
})
const filterChannelName = ref(undefined)
const anySearchOrFilterIsApplied = ref(false)

// ── Delete-customer state ─────────────────────────────────────────────────────
const deleteCustomerConversationId = ref(undefined)

// ── Constants ─────────────────────────────────────────────────────────────────
const instructions = {
  countrycode: "Mobile number country code (eg.: '65' for Singapore)",
  mobileNumber: 'Mobile number'
}

// ── Computed ──────────────────────────────────────────────────────────────────
const filteredChannels = computed(() =>
  [
    { key: 'whatsapp', label: 'WhatsApp', image: whatsappIcon, isConnected: whatsappConnected.value },
    { key: 'googlercs', label: 'Google RCS', image: googleRcsIcon, isConnected: googlercsConnected.value },
    { key: 'sms', label: 'SMS', image: smsLogo, isConnected: smsConnected.value },
  ].filter((c) => c.isConnected),
)

/**
 * The count shown to the pagination component.
 * Uses the filtered total when any search/filter is active, otherwise the global total.
 */
const formattedTotalCustomers = computed(() =>
  anySearchOrFilterIsApplied.value || filterChannelName.value !== undefined
    ? filteredTotalCustomers.value
    : totalCustomers.value,
)

// ── Search helpers ────────────────────────────────────────────────────────────

/**
 * Translates the reactive `search` object into the query-param shape
 * expected by the list API. Returns a channel fallback when a channel
 * filter pill is active but no text-search is in progress.
 */
function buildSearchParams() {
  const {
    searchByFirstName: first_name,
    searchByLastName: last_name,
    searchByEmail: email,
    searchByMobile: mobile_number,
    searchByTagText: tags,
    searchByChannelText: channel,
    searchBySubscriptionStatus: subscription_status,
    segment: segmentid,
    searchByCustomFieldKey: name,
    searchByCustomFieldText: value,
  } = search

  if (first_name) return { first_name }
  if (last_name) return { last_name }
  if (email) return { email }
  if (mobile_number) return { mobile_number }
  if (channel) return { channel: channel.toLowerCase() }
  if (subscription_status) return { subscription_status }
  if (tags) return { tags }
  if (name && value) return { custom_tags: JSON.stringify([{ name, value }]) }
  if (segmentid) return { segmentid }
  if (filterChannelName.value !== undefined) return { channel: filterChannelName.value }
  return {}
}

function resetSearchState() {
  Object.keys(search).forEach((key) => (search[key] = ''))
  filterChannelName.value = undefined
  anySearchOrFilterIsApplied.value = false
}

// ── Orchestration: the three fetch patterns ───────────────────────────────────

/**
 * Full refresh — list + global stats.
 * Use on mount, after delete, and after clear-filter.
 * Stats are always re-fetched here because the totals may have changed.
 */
async function refreshAll() {
  const searchParams = buildSearchParams()
  await Promise.all([fetchList(searchParams, 1, pageSize.value), fetchTotalStats()])
}

/**
 * Search refresh — list + filtered count only (no global stats re-fetch).
 * Global stats don't change when a search filter is applied.
 */
async function applySearch() {
  const searchParams = buildSearchParams()
  await Promise.all([fetchList(searchParams, 1, pageSize.value), fetchFilteredStats(search)])
}

/**
 * Pagination-only refresh — list only, no stats of any kind.
 * The count for the current search hasn't changed; only the page has.
 */
async function loadPage({ page, pageSize }) {
  const searchParams = buildSearchParams()
  await fetchList(searchParams, page, pageSize)
}

// ── Event handlers ────────────────────────────────────────────────────────────

function clearFilter() {
  resetSearchState()
  clearList()
  refreshAll()
}

function onSearchBarSearch(params) {
  search.searchByFirstName = params.searchByFirstNameText || ''
  search.searchByLastName = params.searchByLastNameText || ''
  search.searchByEmail = params.searchByEmailText || ''
  search.searchByMobile = params.searchByMobileNumberText || ''
  search.searchByTagText = params.searchByTagText || ''
  search.searchByCustomFieldKey = params.searchByCustomFieldKey || ''
  search.searchByCustomFieldText = params.searchByCustomFieldText || ''
  search.searchByChannelText = params.searchByChannelText || ''
  search.searchBySubscriptionStatus = params.searchBySubscriptionStatus || ''
  search.segment = params.searchBySegmentId || ''
  filterChannelName.value = undefined

  // If all search params are empty after updating, treat as a full clear
  // to avoid showing stale 0 counts from a previous filtered search
  const hasAnySearchParam = Object.values(search).some(v => v !== '')
  if (!hasAnySearchParam) {
    anySearchOrFilterIsApplied.value = false
    refreshAll()
    return
  }

  anySearchOrFilterIsApplied.value = true
  applySearch()
}

function onSearchBarClear() {
  clearFilter()
}

/**
 * Filters the list by clicking a channel stat card.
 * Delegates chip/display state to CommonSearchFilter via selectChannel(),
 * while pre-populating the count (already known) to avoid an extra API call.
 */
function filterChannel(name, count) {
  const channel = filteredChannels.value.find(c => c.key === name)
  resetSearchState()
  filterChannelName.value = name
  setFilteredTotal(count)
  anySearchOrFilterIsApplied.value = true
  customerSearchFilter.value?.selectChannel(channel || { key: name, label: name })
  fetchList(buildSearchParams(), 1, pageSize.value)
}

function exportCustomers() {
  // Gate on the loaded list (not the slower count API) so Export works even
  // before the count resolves.
  if (customersResults.value.length > 0) {
    openExportCustomers()
  } else {
    notifyExportUnavailable({ statsLoading: statsLoading.value })
  }
}

function deleteCustomer(id) {
  deleteCustomerConversationId.value = id
}

async function confirmDeleteCustomer() {
  try {
    updateLoading({ isLoading: true, message: 'Deleting customer...' })
    await apiInterceptor.put(
      environment('VUE_APP_API_URL') + 'crm/v1/redact/' + deleteCustomerConversationId.value,
      {},
    )
    updateToast({ open: true, message: 'Customer deleted Successfully', type: 'success' })
    await refreshAll()
  } catch (error) {
    updateToast({ open: true, message: error.message, type: 'danger' })
  } finally {
    updateLoading({ isLoading: false })
    deleteCustomerConversationId.value = undefined
  }
}

function onCustomerDetailBack() {
  backToList()
  refreshAll()
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  refreshAll()
  fetchCustomFieldsCount()
  fetchTagsCount()
})
</script>

<style scoped>
.custPadding {
  padding-bottom: 5px;
}
.customer-search-container {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  flex-direction: row;
  flex-shrink: 0;
  gap: 10px;
  border-bottom: 1px solid var(--background-primary);
}
.customer-common-search {
  flex: 1;
  min-width: 0;
}
/* Override CommonSearchFilter liveChatSearchBarParentContainer border for customers page */
.customer-common-search :deep(.liveChatSearchBarParentContainer) {
  border-bottom: none;
}
.customer-common-search :deep(.searchFilter-short) {
  padding-inline-start: 80px !important;
}
.customer-common-search :deep(.liveChatSearchBar) {
  border-right: none;
  padding: 0px;
  margin: 20px 0px 0px 22px;
  background: transparent;
  width: 40%;
}
.customer-common-search :deep(.searchBar) {
  background: var(--white);
}
.customer-common-search :deep(.searchByChips) {
  left: 48px;
}
.customer-common-search :deep(.inbox-search-chip) {
  left: 10px !important;
}
.customer-common-search :deep(.seachByContainer) {
  margin: 15px 0px 0px 20px;
}
.customer-search-container .importContainer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 20px 15px 0px;
  flex-shrink: 0;
}
.count {
  margin-bottom: 20px;
  background: var(--white);
  border-radius: 5px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}
.salesChannelWrapper {
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 10px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.salesChannelWrapper::-webkit-scrollbar {
  display: none;
}
.salesChannleList {
  display: flex;
  gap: 20px;
  justify-content: space-between;
  min-width: max-content;
}
.salesChannleList .customerFlex {
  flex: 1 1 auto;
}
.count .customerFlex {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 70px;
}
.count .total {
  color: var(--black);
  font-size: 14px;
  font-weight: bold;
}
.emptyImg {
  max-width: 200px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}
.listContainer {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.popover-item {
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  color: var(--black);
  transition: background 0.2s;
}
.popover-item:hover {
  background: var(--background-primary);
}
.sub-header {
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
}
.loadingScreen {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80vh;
}
.channelIcon {
  width: 25px;
  height: 25px;
}
.iconDivider {
  border-right: 1px solid var(--background-primary);
}
.flexContainer {
  justify-content: space-between;
}
.customer-listings-content {
  margin: 0px 20px 0px 20px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}
.customer-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: hidden;
}
.customer-listings-wrapper {
  flex-grow: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.customers-info {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}
.customer-list-scrollable {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}
.inner-container {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.detailCustomer {
  padding: 20px;
}
.sticky-pagination {
  margin-bottom: 15px;
}
.chat-toolbar {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid var(--background-primary);
  background: var(--white);
}
.toolbar-start {
  display: flex;
  align-items: center;
  gap: 12px;
}
.backBttn {
  color: var(--black);
}

/* Skeleton loading */
.skeleton-text {
  display: inline-block;
  background: linear-gradient(90deg, var(--background-primary) 25%, #e8e8e8 50%, var(--background-primary) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-circle {
  border-radius: 50%;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive */
.desktop-only {
  display: flex;
}
.mobile-only {
  display: none;
}
@media only screen and (max-width: 760px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .count {
    overflow-x: auto;
  }
  .flex-between {
    display: block;
  }
}
</style>
